import React, { useState } from 'react';
import { apiService, downloadBlob } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Download,
  Users,
  Target,
  TrendingUp,
  Package,
  Loader2,
  Calendar
} from 'lucide-react';

export default function ExportsPage() {
  const [loading, setLoading] = useState({});
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });

  const handleExport = async (type) => {
    setLoading({ ...loading, [type]: true });
    
    try {
      const date = new Date().toISOString().split('T')[0];
      let response;
      let filename;

      switch (type) {
        case 'students':
          response = await apiService.exportStudents({});
          filename = `mtss_students_${date}.csv`;
          break;
        case 'interventions':
          response = await apiService.exportInterventions({});
          filename = `mtss_interventions_${date}.csv`;
          break;
        case 'progress':
          const params = {};
          if (dateRange.start) params.start_date = dateRange.start;
          if (dateRange.end) params.end_date = dateRange.end;
          response = await apiService.exportProgress(params);
          filename = `mtss_progress_${date}.csv`;
          break;
        default:
          return;
      }

      downloadBlob(response.data, filename);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully`);
    } catch (error) {
      console.error(`Failed to export ${type}:`, error);
      toast.error(`Failed to export ${type}`);
    } finally {
      setLoading({ ...loading, [type]: false });
    }
  };

  const exportCards = [
    {
      id: 'students',
      icon: Users,
      title: 'Export All Students',
      description: 'Download a CSV file containing all student profiles with their current status, tier, and basic information.',
      fields: 'Includes: ID, Name, Grade, Teacher, School, Concern Area, Tier, Status, Baseline, Goal, Dates'
    },
    {
      id: 'interventions',
      icon: Target,
      title: 'Export All Interventions',
      description: 'Download a CSV file containing all interventions across all students.',
      fields: 'Includes: ID, Student ID, Intervention Name, Target Skill, Provider, Frequency, Duration, Status'
    },
    {
      id: 'progress',
      icon: TrendingUp,
      title: 'Export All Progress Data',
      description: 'Download a CSV file containing all progress monitoring data points.',
      fields: 'Includes: ID, Student ID, Date, Measure, Score, Unit, Note, Entered By'
    }
  ];

  return (
    <div className="page-container" data-testid="exports-page">
      <div className="page-header">
        <h1 className="page-title">Data Exports</h1>
        <p className="page-subtitle">Export your data as CSV files for backup or analysis</p>
      </div>

      {/* Date Range Filter for Progress */}
      <div className="card mb-6">
        <div className="card-header">
          <h3 className="card-title flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Date Range Filter (for Progress Export)
          </h3>
        </div>
        <div className="card-content">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="w-[180px]"
                data-testid="start-date-filter"
              />
            </div>
            <div>
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="w-[180px]"
                data-testid="end-date-filter"
              />
            </div>
            <Button
              variant="ghost"
              onClick={() => setDateRange({ start: '', end: '' })}
              data-testid="clear-dates-btn"
            >
              Clear Dates
            </Button>
          </div>
          <p className="text-sm text-[#595959] mt-2">
            Leave dates empty to export all progress data
          </p>
        </div>
      </div>

      {/* Export Cards */}
      <div className="grid gap-4">
        {exportCards.map((card) => (
          <div
            key={card.id}
            className="export-card"
            onClick={() => !loading[card.id] && handleExport(card.id)}
            data-testid={`export-${card.id}-card`}
          >
            <div className="export-icon">
              <card.icon size={24} />
            </div>
            <div className="export-info">
              <div className="export-title">{card.title}</div>
              <div className="export-desc">{card.description}</div>
              <div className="text-xs text-[#A3A3A3] mt-1">{card.fields}</div>
            </div>
            <Button
              className="bg-[#2C4C3B] hover:bg-[#1E3629]"
              disabled={loading[card.id]}
              onClick={(e) => {
                e.stopPropagation();
                handleExport(card.id);
              }}
              data-testid={`export-${card.id}-btn`}
            >
              {loading[card.id] ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </>
              )}
            </Button>
          </div>
        ))}
      </div>

      {/* File Naming Info */}
      <div className="card mt-6">
        <div className="card-content">
          <h4 className="font-medium text-[#1C1C1C] mb-2">File Naming Convention</h4>
          <p className="text-sm text-[#595959]">
            All exported files are named with the current date for easy organization:
          </p>
          <ul className="text-sm text-[#595959] mt-2 space-y-1">
            <li><code className="bg-[#F3F4F1] px-1 rounded">mtss_students_YYYY-MM-DD.csv</code></li>
            <li><code className="bg-[#F3F4F1] px-1 rounded">mtss_interventions_YYYY-MM-DD.csv</code></li>
            <li><code className="bg-[#F3F4F1] px-1 rounded">mtss_progress_YYYY-MM-DD.csv</code></li>
          </ul>
        </div>
      </div>
    </div>
  );
}
